import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Spinner } from './spinner';

export type StepState = 'pending' | 'active' | 'done' | 'error';

export interface Step {
  label: string;
  description?: string;
  /** Right-aligned status hint, e.g. "2 / 3" or a small note. */
  hint?: ReactNode;
  state: StepState;
}

/**
 * A vertical numbered sequence of stages, each moving through
 * pending → active → done (or error). The active step spins; completed steps
 * fill with the brand green. Connector lines tie the sequence together.
 */
export function Stepper({
  steps,
  className,
  spin = true,
}: {
  steps: Step[];
  className?: string;
  /** Whether the active step spins. Set false for a plain highlighted number —
   * use it for a "you are here" progress guide (not a working spinner). */
  spin?: boolean;
}) {
  return (
    <ol className={cn('space-y-0', className)}>
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <div className="flex flex-none flex-col items-center">
            <StepMarker index={i + 1} state={step.state} spin={spin} />
            {i < steps.length - 1 && (
              <span
                className={cn('w-px flex-1', step.state === 'done' ? 'bg-iris/50' : 'bg-hairline')}
              />
            )}
          </div>
          <div className={cn('min-w-0 flex-1', i < steps.length - 1 && 'pb-4')}>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-sm font-semibold',
                  step.state === 'pending' ? 'text-muted' : 'text-bone',
                )}
              >
                {step.label}
              </span>
              {step.hint != null && (
                <span className="ml-auto font-mono text-[0.7rem] text-muted">{step.hint}</span>
              )}
            </div>
            {step.description && (
              <p className="mt-0.5 text-xs leading-snug text-haze">{step.description}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The circular step marker: number → spinner (active) → ✓ (done) → ✕ (error).
 * `spin={false}` keeps the active step a calm highlighted number (no spinner) —
 * for progress trackers where the active step isn't "working" yet. */
export function StepMarker({
  index,
  state,
  spin = true,
}: {
  index: number;
  state: StepState;
  spin?: boolean;
}) {
  const base =
    'grid h-7 w-7 place-items-center rounded-full border text-xs font-bold transition-colors';
  if (state === 'done')
    return <span className={cn(base, 'border-transparent bg-iris text-on-iris')}>✓</span>;
  if (state === 'error')
    return <span className={cn(base, 'border-transparent bg-danger text-white')}>✕</span>;
  if (state === 'active')
    return (
      <span className={cn(base, 'border-iris bg-iris-dim text-iris ring-4 ring-iris-dim')}>
        {spin ? <Spinner size="sm" /> : index}
      </span>
    );
  return <span className={cn(base, 'border-border text-muted')}>{index}</span>;
}
